import React, { useCallback, useEffect, useState } from 'react';
import { IngredientCard } from 'components/customComponents/Layers/InputGroups';
import { ReactComponent as Edit } from 'Icons/CommandPanel/Edit.svg';
import { ReactComponent as Delete } from 'Icons/CommandPanel/Delete.svg';
import { ReactComponent as AddIngredient } from 'Icons/Sidebar/AddIngredient.svg';
import { RandomLayerIngProps } from 'business/models/CustomModels';
import GroupContainer from './Group/GroupsContainer';
import { getIngredientName, getIngredientStock, isNullOrUndefined } from 'utils/Global';
import ELayersView from 'enums/ELayersView';
import {
  LayerDTO,
  RandomLayerDTO,
  RandomLayerGenerationListDTO,
  RandomLayerGroupIngredientDTO,
  RandomLayerIngredientGroupDTO,
} from 'business/models/Models';
import { usePreserveExperiment, useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectIsEditable,
  selectIsModifyAccessRights,
} from 'redux/selectors/StateSelectors';
import { updateExperimentLayers } from 'redux/reducers/experimentDataReducer';
import {
  selectExperimentLayers,
  selectSelectedLayerId,
  selectExp,
} from 'redux/selectors/ExperimentStateSelector';
import { cloneDeep } from 'lodash';
import { useIsScreenInUse, useIsScreenIngredientEditable } from 'redux/hooks/common';
import { useAllowLayerEditing } from 'redux/hooks/experiment';

export default function RandomLayersIngredients({
  setContentView,
  setEditIngredientData,
  setSelectedIngredient,
  setSelectedStocks,
  activeGroupId,
  setActiveGroupId,
}: RandomLayerIngProps) {
  const [refresh, setRefresh] = useState<boolean>(false);
  const _selectedLayerId = useRMSelector(selectSelectedLayerId);
  const experimentLayers = useRMSelector(selectExperimentLayers);
  const _exp = useRMSelector(selectExp);
  const [groups, setGroups] = useState<RandomLayerIngredientGroupDTO[]>([]);

  const dispatch = useRMDispatch();
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const modifyAccessRights: boolean = useRMSelector(selectIsModifyAccessRights);
  const setLayers = useCallback(
    (layers: LayerDTO[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const isScreenInUse: boolean = useIsScreenInUse();
  const isLayerEditingAllowed = useAllowLayerEditing();
  const isScreenEditable = useIsScreenIngredientEditable();

  useEffect(() => {
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);

    setGroups((selectedLayer.layerDetail as RandomLayerDTO).randomLayerIngredientGroup);
  }, [_selectedLayerId, _exp]);

  function onClickEditIngredient(ing: RandomLayerGroupIngredientDTO) {
    setSelectedStocks(getIngredientStock(ing.ingredientStockId));
    const ingName = getIngredientName(ing.ingredientIngredientTypeId);
    setSelectedIngredient({
      id: ing.ingredientIngredientTypeId,
      name: ingName,
    });
    setEditIngredientData(ing);
    setContentView(ELayersView.EDITINGREDIENTSVIEW);
  }
  function onClickAddIngredient(g: RandomLayerIngredientGroupDTO) {
    setEditIngredientData(
      new RandomLayerGroupIngredientDTO({
        id: null,
        ingredientIngredientTypeId: null,
        randomLayerIngredientGroupId: g.id,
        ingredientStockId: null,
        lowConcentration: null,
        highConcentration: null,
        weight: 1,
      }),
    );
    setContentView(ELayersView.SELECTINGREDIENTSVIEW);
  }
  function setIngredientsinGroupCard(
    group: RandomLayerIngredientGroupDTO,
    ings,
    activeGroupId: number,
  ) {
    return (
      <div id="commandpanel-randomlayering-groupcard-wrapper" className="group-card">
        <div
          id="commandpanel-randomlayering-groupcard-container"
          className="no-border-ingredient-card"
        >
          <div
            id="commandpanel-randomlayering-groupcard-title-row"
            className="flex flex-row ing-content padding0"
          >
            <div
              id="commandpanel-randomlayering-groupcard-title"
              className="marginRight20"
            >
              {group.name}
            </div>
            {isEditMode && modifyAccessRights && isLayerEditingAllowed && (
              <AddIngredient
                id="commandpanel-randomlayering-groupcard-adding"
                className={`marginRight20 ${
                  isScreenEditable
                    ? 'cursor-pointer'
                    : 'cursor-not-allowed opacity-40 pointer-events-none'
                }`}
                onClick={isScreenEditable ? () => onClickAddIngredient(group) : undefined}
              />
            )}
          </div>
          {isEditMode &&
            modifyAccessRights &&
            !isScreenInUse &&
            isLayerEditingAllowed && (
              <div
                id="commandpanel-randomlayering-groupcard-actions"
                className="flex ing-delete-update flex-space-around [&_path]:fill-black"
              >
                <Edit
                  id="commandpanel-randomlayering-groupcard-edit"
                  onClick={() => setContentView(ELayersView.EDITGROUPSVIEWEDIT)}
                  className="cursor-pointer hover:opacity-70"
                />
                <Delete
                  id="commandpanel-randomlayering-groupcard-delete"
                  onClick={(e) => deleteGroup(activeGroupId)}
                  className="cursor-pointer hover:opacity-70"
                />
              </div>
            )}
        </div>
        <div
          id="commandpanel-randomlayering-groupcard-ingredients"
          className="group-card-ingredients"
        >
          {ings}
        </div>
      </div>
    );
  }

  function deleteGroup(id) {
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);

    for (const rlig of (selectedLayer.layerDetail as RandomLayerDTO)
      .randomLayerIngredientGroup) {
      const i = rlig.randomLayerGenerationList.findIndex((g) => g.id === id);
      if (i >= 0) {
        rlig.randomLayerGenerationList.splice(i, 1);
        break;
      }
    }
    selectedLayer.dirty = true;
    callPreserveUndoableState();
    setLayers(layers);
    setGroups((selectedLayer.layerDetail as RandomLayerDTO).randomLayerIngredientGroup);
    setActiveGroupId(-1);
    setRefresh(!refresh);
  }
  function addGroup() {
    const groupsArr: RandomLayerIngredientGroupDTO[] = [];
    groups.map((g) => {
      groupsArr.push(g);
    });
    const newGroup: RandomLayerIngredientGroupDTO = new RandomLayerIngredientGroupDTO();
    newGroup.id = (groups.length + 2) * -1;
    newGroup.name = `Group ${groups.length + 2}`;
    groupsArr.push(newGroup);
    setGroups(groupsArr);
  }

  function deleteIngredientInSelectedGroup(ingredient: RandomLayerGroupIngredientDTO) {
    const layers = cloneDeep(experimentLayers);
    const selectedLayer = layers.find((lyr) => lyr.id === _selectedLayerId);

    for (const rlig of (selectedLayer.layerDetail as RandomLayerDTO)
      .randomLayerIngredientGroup) {
      const i = rlig.randomLayerGenerationList.findIndex((g) => g.id === activeGroupId);
      if (i >= 0) {
        const ingIndex = rlig.randomLayerGroupIngredient.findIndex(
          (rlgi) => rlgi.id === ingredient.id,
        );
        rlig.randomLayerGroupIngredient.splice(ingIndex, 1);
        break;
      }
    }
    selectedLayer.dirty = true;
    callPreserveUndoableState();
    setLayers(layers);
    setGroups((selectedLayer.layerDetail as RandomLayerDTO).randomLayerIngredientGroup);
    setRefresh(!refresh);
  }

  function getSelectedGroupIngredients() {
    if (isNullOrUndefined(groups)) {
      return null;
    }
    let selectedGroup: RandomLayerIngredientGroupDTO;
    for (const g of groups) {
      const i = g.randomLayerGenerationList.findIndex((g) => g.id === activeGroupId);
      if (i >= 0) {
        selectedGroup = g;
        break;
      }
    }
    if (isNullOrUndefined(selectedGroup)) {
      return null;
    } else if (isNullOrUndefined(selectedGroup.randomLayerGroupIngredient)) {
      return setIngredientsinGroupCard(selectedGroup, null, activeGroupId);
    }
    let layerIngGroup: RandomLayerGenerationListDTO;
    selectedGroup.randomLayerGenerationList.map((rLayer) => {
      if (isNullOrUndefined(layerIngGroup)) {
        layerIngGroup = rLayer;
      }
      if (rLayer.probability > layerIngGroup.probability) {
        layerIngGroup = rLayer;
      }
    });
    const ings = [];
    selectedGroup.randomLayerGroupIngredient.map((i) => {
      ings.push(
        <IngredientCard
          id="commandpanel-randomlayering-groupcard-ingredientcard"
          ingredientData={i}
          setSelectedIngredient={setSelectedIngredient}
          setEditIngredientData={setEditIngredientData}
          setContentView={setContentView}
          onEditIngredient={() => onClickEditIngredient(i)}
          onDeleteIngredient={() => deleteIngredientInSelectedGroup(i)}
          probability={layerIngGroup.probability}
          isEditMode={isEditMode && modifyAccessRights && isLayerEditingAllowed}
        />,
      );
    });

    return setIngredientsinGroupCard(selectedGroup, ings, activeGroupId);
  }
  return (
    <>
      <GroupContainer
        setContentView={setContentView}
        groups={groups}
        deleteGroup={deleteGroup}
        setActiveGroupId={setActiveGroupId}
        activeGroupId={activeGroupId}
        addGroup={addGroup}
        layerData={experimentLayers.find((l) => l.id === _selectedLayerId)}
        setEditIngredientData={setEditIngredientData}
        isEditMode={isEditMode && modifyAccessRights && isLayerEditingAllowed}
      />
      {getSelectedGroupIngredients()}
    </>
  );
}
